/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { Aufgabe } from '../models/Aufgabe';
import type { AufgabeDaten } from '../models/AufgabeDaten';
import type { AufgabenListe } from '../models/AufgabenListe';
import type { AuswertungDataResponse } from '../models/AuswertungDataResponse';
import type { PromptTemplate } from '../models/PromptTemplate';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class AufgabenService {

  constructor(public readonly httpRequest: BaseHttpRequest) {}

  /**
   * Eine Liste aller Aufgaben
   * Gibt eine Liste aller Aufgaben zurück
   * @returns AufgabenListe Liste der gesuchten Aufgaben
   * @throws ApiError
   */
  public searchAufgaben({
    search,
    filtered,
    page,
    limit,
    sort,
  }: {
    /**
     * Suchstring für Aufgaben
     */
    search?: string,
    /**
     * Filterstring für Aufgaben Seite
     */
    filtered?: string,
    /**
     * Seitenzahl der Aufgaben Seite
     */
    page?: number,
    /**
     * Limit der Liste von Aufgaben Einträgen
     */
    limit?: number,
    /**
     * Sortierung der Aufgaben Seite
     */
    sort?: string,
  }): CancelablePromise<AufgabenListe> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/api/v1/aufgaben',
      query: {
        'search': search,
        'filtered': filtered,
        'page': page,
        'limit': limit,
        'sort': sort,
      },
      errors: {
        400: `Bad Request for this request (searchAufgaben)`,
        401: `Unauthorized for this request (searchAufgaben)`,
        403: `Forbidden for this request (searchAufgaben)`,
        404: `Not Found for this request (searchAufgaben)`,
      },
    });
  }

  /**
   * Eine Aufgabe erstellen
   * Erstellt eine Aufgabe
   * @returns Aufgabe Die erstellte Aufgabe
   * @throws ApiError
   */
  public createAufgabe({
    requestBody,
  }: {
    /**
     * Daten, mit welchen die Aufgabe erstellt werden soll
     */
    requestBody: AufgabeDaten,
  }): CancelablePromise<Aufgabe> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/api/v1/aufgaben',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        401: `Unauthorized for this request (createAufgabe)`,
        403: `Forbidden for this request (createAufgabe)`,
      },
    });
  }

  /**
   * Prompt Template für eine Aufgabe holen
   * Holt das Prompt Template für eine Aufgabe
   * @returns PromptTemplate Das Prompt Template der Aufgabe
   * @throws ApiError
   */
  public getAufgabePromptTemplate({
    languagecode,
  }: {
    /**
     * Sprachcode für das Prompt Template (z.B. "de" oder "en") für (getAufgabePromptTemplate)
     */
    languagecode: string,
  }): CancelablePromise<PromptTemplate> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/api/v1/aufgaben/getprompttemplate/{languagecode}',
      path: {
        'languagecode': languagecode,
      },
      errors: {
        401: `Unauthorized for this request (getAufgabePromptTemplate)`,
        403: `Forbidden for this request (getAufgabePromptTemplate)`,
        404: `Not Found for this request (getAufgabePromptTemplate)`,
      },
    });
  }

  /**
   * Eine Aufgabe bekommen
   * Bekomme eine Aufgabe
   * @returns Aufgabe Die Aufgabe
   * @throws ApiError
   */
  public getAufgabe({
    aufgabeId,
  }: {
    /**
     * Eindeutige ID der Aufgabe für (getAufgabe)
     */
    aufgabeId: string,
  }): CancelablePromise<Aufgabe> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/api/v1/aufgaben/{aufgabeId}',
      path: {
        'aufgabeId': aufgabeId,
      },
      errors: {
        401: `Unauthorized for this request (getAufgabe)`,
        403: `Forbidden for this request (getAufgabe)`,
        404: `Not Found for this request (getAufgabe)`,
      },
    });
  }

  /**
   * Eine Aufgabe aktualisieren
   * Aktualisiert eine Aufgabe
   * @returns Aufgabe Die aktualisierte Aufgabe
   * @throws ApiError
   */
  public updateAufgabe({
    aufgabeId,
    requestBody,
  }: {
    /**
     * Eindeutige ID der Aufgabe für (updateAufgabe)
     */
    aufgabeId: string,
    /**
     * Daten, mit welchen die Aufgabe aktualisiert werden soll
     */
    requestBody: AufgabeDaten,
  }): CancelablePromise<Aufgabe> {
    return this.httpRequest.request({
      method: 'PUT',
      url: '/api/v1/aufgaben/{aufgabeId}',
      path: {
        'aufgabeId': aufgabeId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        401: `Unauthorized for this request (updateAufgabe)`,
        403: `Forbidden for this request (updateAufgabe)`,
        404: `Not Found for this request (updateAufgabe)`,
      },
    });
  }

  /**
   * Eine Aufgabe löschen
   * Löscht eine Aufgabe
   * @returns Aufgabe Die gelöschte Aufgabe
   * @throws ApiError
   */
  public deleteAufgabe({
    aufgabeId,
  }: {
    /**
     * Eindeutige ID der Aufgabe für (deleteAufgabe)
     */
    aufgabeId: string,
  }): CancelablePromise<Aufgabe> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/api/v1/aufgaben/{aufgabeId}',
      path: {
        'aufgabeId': aufgabeId,
      },
      errors: {
        401: `Unauthorized for this request (deleteAufgabe)`,
        403: `Forbidden for this request (deleteAufgabe)`,
        404: `Not Found for this request (deleteAufgabe)`,
      },
    });
  }

  /**
   * Eine Aufgabe duplizieren
   * dupliziert eine Aufgabe
   * @returns Aufgabe Die duplizierte Aufgabe
   * @throws ApiError
   */
  public duplicateAufgabe({
    aufgabeId,
  }: {
    /**
     * Eindeutige ID der Aufgabe für (duplicateAufgabe)
     */
    aufgabeId: string,
  }): CancelablePromise<Aufgabe> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/api/v1/aufgaben/{aufgabeId}/duplicate',
      path: {
        'aufgabeId': aufgabeId,
      },
      errors: {
        401: `Unauthorized for this request (getAufgabe)`,
        403: `Forbidden for this request (getAufgabe)`,
        404: `Not Found for this request (getAufgabe)`,
      },
    });
  }

  /**
   * Der Export für eine Aufgabe als CSV (Legacy-Format)
   * Gibt eine Auswertung der Aufgabe als CSV zurück (alte Struktur mit fixen Spalten für Unteraufgaben)
   * @returns binary Auswertung der Aufgabe als CSV-Datei
   * @throws ApiError
   */
  public searchUnteraufgabenAuswertungen({
    aufgabeId,
  }: {
    /**
     * Eindeutige ID der Aufgabe
     */
    aufgabeId: string,
  }): CancelablePromise<Blob> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/api/v1/aufgaben/{aufgabeId}/auswertung-export',
      path: {
        'aufgabeId': aufgabeId,
      },
      errors: {
        400: `Bad Request for this request`,
        401: `Unauthorized for this request`,
        403: `Forbidden for this request`,
        404: `Not Found for this request`,
      },
    });
  }

  /**
   * Der Export für eine Aufgabe als CSV (Normalisiertes Format)
   * Gibt eine Auswertung der Aufgabe als CSV zurück (normalisierte Struktur - eine Zeile pro Kontaktaufnahme und Unteraufgabe)
   * @returns binary Auswertung der Aufgabe als CSV-Datei (normalisiert)
   * @throws ApiError
   */
  public searchUnteraufgabenAuswertungenNormalized({
    aufgabeId,
  }: {
    /**
     * Eindeutige ID der Aufgabe
     */
    aufgabeId: string,
  }): CancelablePromise<Blob> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/api/v1/aufgaben/{aufgabeId}/auswertung-export-normalized',
      path: {
        'aufgabeId': aufgabeId,
      },
      errors: {
        400: `Bad Request for this request`,
        401: `Unauthorized for this request`,
        403: `Forbidden for this request`,
        404: `Not Found for this request`,
      },
    });
  }

  /**
   * Auswertungsdaten für eine Aufgabe
   * Gibt die Auswertungsdaten der Aufgabe zurück (strukturiert nach Kontaktaufnahmen mit Aufgabe und Unteraufgaben)
   * @returns AuswertungDataResponse Auswertungsdaten der Aufgabe
   * @throws ApiError
   */
  public getAufgabeAuswertungData({
    aufgabeId,
  }: {
    /**
     * Eindeutige ID der Aufgabe
     */
    aufgabeId: string,
  }): CancelablePromise<AuswertungDataResponse> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/api/v1/aufgaben/{aufgabeId}/auswertung-data',
      path: {
        'aufgabeId': aufgabeId,
      },
      errors: {
        400: `Bad Request for this request`,
        401: `Unauthorized for this request`,
        403: `Forbidden for this request`,
        404: `Not Found for this request`,
      },
    });
  }

  /**
   * Tool zu Aufgabe hinzufügen
   * Fügt ein Tool zu einer Aufgabe hinzu
   * @returns Aufgabe Die aktualisierte Aufgabe
   * @throws ApiError
   */
  public addToolToAufgabe({
    aufgabeId,
    toolId,
  }: {
    aufgabeId: string,
    toolId: string,
  }): CancelablePromise<Aufgabe> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/api/v1/aufgaben/{aufgabeId}/tools/{toolId}',
      path: {
        'aufgabeId': aufgabeId,
        'toolId': toolId,
      },
      errors: {
        404: `Not Found`,
      },
    });
  }

  /**
   * Tool von Aufgabe entfernen
   * Entfernt ein Tool von einer Aufgabe
   * @returns Aufgabe Die aktualisierte Aufgabe
   * @throws ApiError
   */
  public removeToolFromAufgabe({
    aufgabeId,
    toolId,
  }: {
    aufgabeId: string,
    toolId: string,
  }): CancelablePromise<Aufgabe> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/api/v1/aufgaben/{aufgabeId}/tools/{toolId}',
      path: {
        'aufgabeId': aufgabeId,
        'toolId': toolId,
      },
      errors: {
        404: `Not Found`,
      },
    });
  }

}
